﻿import styles from './appCheckboxGroupInput.module.scss';
import React, { useCallback, useMemo } from 'react';
import { AppCheckboxInput } from 'uikit/inputs';
import { FieldsWithType } from '../../../../helpers/typeUtils';
import { AppButton } from 'uikit/buttons';
import { useTranslation } from 'react-i18next';

export type AppCheckboxGroupInputProps<
  TOption extends Record<Key, string | number>,
  Key extends FieldsWithType<TOption, string | number>,
> = {
  options: TOption[];
  valueField: Key;
  labelField: keyof TOption;
  disabled?: boolean;
  defaultValue?: TOption[Key][];
  value?: TOption[Key][];
  onChange?: (value: TOption[Key][] | null) => void;
  numerationStartIndex?: number;
  withEllipsis?: boolean;
  withSelectAllButton?: boolean;
};

export function AppCheckboxGroupInput<
  TOption extends Record<Key, string | number>,
  Key extends FieldsWithType<TOption, string | number>,
>({
  disabled,
  options,
  defaultValue,
  value,
  valueField,
  labelField,
  onChange,
  withEllipsis,
  withSelectAllButton,
}: AppCheckboxGroupInputProps<TOption, Key>) {
  const { t } = useTranslation();
  const onChangeHandler = useCallback(
    (option: TOption) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        onChange?.([...(value || []), option[valueField]]);
        return;
      }

      const newValue = value?.filter((x: any) => x !== option[valueField]);
      onChange?.(newValue?.length ? newValue : null);
    },
    [onChange, value, valueField],
  );

  const isAllOptionsSelected = useMemo(
    () => options?.map((x) => x[valueField])?.every((x) => value?.includes(x)),
    [options, value, valueField],
  );

  const toggleHandler = useCallback(
    () => (isAllOptionsSelected ? onChange?.([]) : onChange?.(options?.map((x) => x[valueField]) ?? [])),
    [isAllOptionsSelected, onChange, options, valueField],
  );

  return (
    <div>
      {withSelectAllButton && (
        <AppButton
          variant={'icon-link'}
          colorSchema={'primary'}
          text={isAllOptionsSelected ? t('Common_None') : t('Common_SelectAll')}
          className={styles.uncheckAllButton}
          disabled={disabled}
          onClick={toggleHandler}
        />
      )}

      {options.map((option) => (
        <div key={option[valueField]} className={styles.checkbox}>
          <AppCheckboxInput
            key={option[valueField]}
            label={option[labelField].toString()}
            disabled={disabled}
            defaultChecked={defaultValue?.includes(option[valueField])}
            checked={!!value?.includes(option[valueField])}
            onChange={onChangeHandler(option)}
            withEllipsis={withEllipsis}
          />
        </div>
      ))}
    </div>
  );
}
